import DOMPurify from 'dompurify';

/**
 * Allowed HTML tags and attributes for job content
 */
const ALLOWED_TAGS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'ul', 'ol', 'li', 'br', 'strong', 'b', 'em', 'i', 'a', 'span', 'div', 'table', 'thead', 'tbody', 'tr', 'th', 'td'];
const ALLOWED_ATTR = ['href', 'target', 'rel'];

function purifyHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
  });
}

/**
 * Detects whether a string is already HTML or plain text
 */
function isHtml(str: string): boolean {
  return /<\/?[a-z][\s\S]*>/i.test(str);
}

/**
 * Common section header patterns in job descriptions
 */
const SECTION_HEADERS = [
  'Key Responsibilities', 'Responsibilities', 'Essential Functions', 'Duties',
  'Requirements', 'Qualifications', 'Required Qualifications', 'Preferred Qualifications',
  'What You Will Do', 'What We Offer', 'What We Look For', 'What we offer',
  'How You Will Do It', 'How you will do it',
  'About Us', 'About The Role', 'About the Company', 'About You',
  'Benefits', 'Compensation', 'Compensation and Benefits',
  'Position Summary', 'General Position Summary', 'Job Summary', 'Summary',
  'Position Requirements', 'Physical Requirements', 'Ergonomic Considerations',
  'Education', 'Experience', 'Skills', 'Certifications',
  'Equal Opportunity', 'EEO Statement',
  'Description', 'Overview', 'Role',
];

/**
 * Converts plain text job content into structured HTML.
 * Handles both multi-line text (split by \n) and single-line continuous text.
 */
function plainTextToHtml(text: string): string {
  // If text has newlines, split on them
  const hasNewlines = text.includes('\n');
  
  let lines: string[];
  if (hasNewlines) {
    lines = text.split(/\n+/).map(l => l.trim()).filter(Boolean);
  } else {
    // Single continuous string: insert line breaks before known section headers
    // Sort headers by length descending so longer matches take priority
    const sortedHeaders = [...SECTION_HEADERS].sort((a, b) => b.length - a.length);
    const SPLIT_MARKER = '|||SPLIT|||';
    
    let processed = text;
    for (const header of sortedHeaders) {
      // Case-insensitive indexOf-based approach to avoid regex issues
      const lowerProcessed = processed.toLowerCase();
      const lowerHeader = header.toLowerCase();
      let searchStart = 0;
      
      while (true) {
        const idx = lowerProcessed.indexOf(lowerHeader, searchStart);
        if (idx === -1) break;
        
        // Check that it's preceded by a space (or start) and followed by ":"
        const beforeOk = idx === 0 || /\s/.test(processed[idx - 1]);
        const afterHeaderEnd = idx + header.length;
        // Find the colon (possibly with spaces between header and colon)
        const afterText = processed.substring(afterHeaderEnd, afterHeaderEnd + 5);
        const colonMatch = afterText.match(/^\s*:/);
        
        if (beforeOk && colonMatch) {
          // Check this spot hasn't already been split
          const beforeText = processed.substring(Math.max(0, idx - SPLIT_MARKER.length), idx);
          if (!beforeText.includes(SPLIT_MARKER)) {
            processed = processed.substring(0, idx) + SPLIT_MARKER + processed.substring(idx);
            searchStart = idx + SPLIT_MARKER.length + header.length + 5;
          } else {
            searchStart = afterHeaderEnd + 1;
          }
        } else {
          searchStart = afterHeaderEnd + 1;
        }
        // Update lowercase version since we modified processed
        break; // Only handle first occurrence per header to keep it simple
      }
    }
    
    let segments = processed.split(SPLIT_MARKER).map(s => s.trim()).filter(Boolean);
    
    // For each segment, split into paragraphs for readability
    lines = [];
    for (const segment of segments) {
      if (segment.length > 400) {
        const sentences = segment.match(/[^.!?]+[.!?]+\s*/g) || [segment];
        let current = '';
        for (const sentence of sentences) {
          current += sentence;
          if (current.length > 250) {
            lines.push(current.trim());
            current = '';
          }
        }
        if (current.trim()) lines.push(current.trim());
      } else {
        lines.push(segment);
      }
    }
  }

  const htmlParts: string[] = [];
  let inList = false;

  for (const line of lines) {
    const lineWithoutColon = line.replace(/:$/, '').trim();
    // Only treat as heading if it matches a known section header (exact match)
    const isKnownHeader = SECTION_HEADERS.some(h => lineWithoutColon.toLowerCase() === h.toLowerCase());
    // Or if it looks like a header: short, starts with caps, ends with colon, has 2+ words
    const looksLikeHeader = line.endsWith(':') && line.length < 60 && /^[A-Z]/.test(line) && 
      lineWithoutColon.split(/\s+/).length >= 2;
    const isHeading = isKnownHeader || looksLikeHeader;

    // Detect list items
    const isBullet = /^[\u2022\u25CF\u25CB\u25AA\u25A0\-\*\•]\s/.test(line) || /^\d+[\.\)]\s/.test(line);

    if (isBullet) {
      if (!inList) { htmlParts.push('<ul>'); inList = true; }
      const content = line.replace(/^[\u2022\u25CF\u25CB\u25AA\u25A0\-\*\•]\s*/, '').replace(/^\d+[\.\)]\s*/, '');
      htmlParts.push(`<li>${content}</li>`);
    } else {
      if (inList) { htmlParts.push('</ul>'); inList = false; }
      if (isHeading) {
        const heading = line.replace(/:$/, '');
        htmlParts.push(`<h2>${heading}</h2>`);
      } else {
        htmlParts.push(`<p>${line}</p>`);
      }
    }
  }
  if (inList) htmlParts.push('</ul>');

  return htmlParts.join('\n');
}

/**
 * Sanitizes and fixes common encoding issues in job HTML content.
 * Uses DOMPurify to prevent XSS attacks.
 */
export function sanitizeJobContent(html: string | null | undefined): string {
  if (!html) return '';

  // If the content is plain text (no HTML tags), convert to HTML first
  if (!isHtml(html)) {
    return purifyHtml(plainTextToHtml(html));
  }
  
  const cleaned = html
    // Fix common UTF-8 encoding issues - using unicode escapes to avoid encoding problems
    .replace(/\u201C/g, '"')      // left double quote
    .replace(/\u201D/g, '"')      // right double quote
    .replace(/\u2018/g, "'")      // left single quote
    .replace(/\u2019/g, "'")      // right single quote
    .replace(/\u2013/g, '-')      // en-dash
    .replace(/\u2014/g, '-')      // em-dash
    .replace(/\u00A0/g, ' ')      // non-breaking space
    // Fix mojibake patterns (common when UTF-8 is decoded as Latin-1)
    .replace(/â€"/g, '-')
    .replace(/â€™/g, "'")
    .replace(/â€œ/g, '"')
    .replace(/â€/g, '"')
    .replace(/Ã¢â‚¬â„¢/g, "'")
    .replace(/Ã¢â‚¬"/g, '-')
    // Remove empty tags
    .replace(/<h[2-6]>\s*<\/h[2-6]>/gi, '')
    .replace(/<p>\s*<\/p>/gi, '')
    .replace(/<p style="[^"]*">\s*<\/p>/gi, '')
    // Strip inline styles for consistent styling
    .replace(/style="[^"]*"/gi, '')
    // Clean up multiple line breaks
    .replace(/(<br\s*\/?>\s*){3,}/gi, '<br/><br/>')
    // Trim whitespace
    .trim();

  return purifyHtml(cleaned);
}

/**
 * Extracts a plain text excerpt from HTML for meta descriptions
 */
export function extractTextExcerpt(html: string | null | undefined, maxLength: number = 160): string {
  if (!html) return '';
  
  // Remove HTML tags
  const text = html
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  
  if (text.length <= maxLength) return text;
  
  // Truncate at word boundary
  const truncated = text.substring(0, maxLength);
  const lastSpace = truncated.lastIndexOf(' ');
  
  return (lastSpace > 0 ? truncated.substring(0, lastSpace) : truncated) + '...';
}
